import { Plane, CircleDot, Circle } from "lucide-react";
import {
  formatTime,
  formatDuration,
  formatPrice,
  arrivesNextDay,
} from "@/components/trips/itinerary/itinerary-utils";
import { getAirportInfo } from "@/lib/airports";
import type { PlanFlight } from "@/lib/types";
import SourceLed from "./SourceLed";
import SegmentTimeline from "./SegmentTimeline";
import FlightExtras from "./FlightExtras";

/* Per-flight detail panel — modelled on Google Flights' expandable detail
   (vertical segment timeline + aircraft line) but rendered ONLY from data we
   actually have. We never fabricate layover airports/times, fare brands,
   emissions, or amenities: for multi-stop flights we show an honest
   "coming soon" note instead. */

interface FlightDetailProps {
  flight: PlanFlight;
  /** Origin airport IATA (from the leg's route). */
  originCode: string | null;
  /** Destination airport IATA (from the leg's route). */
  destinationCode: string | null;
}

function stopsLabel(stops: number | null): string {
  if (stops == null) return "Stops unknown";
  if (stops === 0) return "Nonstop";
  return stops === 1 ? "1 stop" : stops + " stops";
}

/** "Dallas/Fort Worth Intl (DFW)" — full name + code; null-safe. */
function airportLine(code: string | null): { name: string; code: string } {
  const raw = (code || "").trim().toUpperCase();
  if (!raw) return { name: "Airport not available", code: "" };
  const info = getAirportInfo(raw);
  // Unknown codes resolve name===code; show the code as the name then.
  return { name: info.name, code: raw };
}

export default function FlightDetail({
  flight,
  originCode,
  destinationCode,
}: FlightDetailProps) {
  const origin = airportLine(originCode);
  const dest = airportLine(destinationCode);

  const departT = formatTime(flight.departLocal) || "—";
  const arriveT = formatTime(flight.arriveLocal) || "—";
  const nextDay = arrivesNextDay(flight.departDate, flight.arriveDate);

  const dur = formatDuration(flight.durationMinutes);
  const airlineName = flight.airlineName || flight.airline || "Flight";
  const flightNo = flight.flightNumber || null;
  const aircraft =
    flight.aircraft && flight.aircraft.trim()
      ? flight.aircraft.trim()
      : "Aircraft not available";
  const cabin = flight.cabin && flight.cabin.trim() ? flight.cabin.trim() : null;
  const priceLabel = formatPrice(flight.price);
  const multiStop = (flight.stops ?? 0) > 0;

  // Real per-segment data, when the backend enrichment supplies it. Absent in
  // the common case today → we degrade to the honest origin→dest summary below.
  const segments = flight.segments?.length ? flight.segments : null;

  return (
    <div className="space-y-3 rounded-lg border bg-muted/20 p-3 text-sm">
      {segments ? (
        /* Real per-segment vertical timeline (origin → … → destination). */
        <SegmentTimeline segments={segments} />
      ) : (
        /* Honest origin → destination summary when we have no segments. */
        <ol className="relative space-y-3 pl-1">
        {/* Depart */}
        <li className="flex gap-2.5">
          <span className="mt-0.5 flex flex-col items-center" aria-hidden="true">
            <CircleDot className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />
            <span className="my-0.5 h-6 w-px bg-border" />
          </span>
          <div className="min-w-0">
            <div className="font-mono tabular-nums font-semibold text-foreground">{departT}</div>
            <div className="truncate text-muted-foreground">
              {origin.name}
              {origin.code && (
                <span className="ml-1 font-mono tabular-nums">({origin.code})</span>
              )}
            </div>
          </div>
        </li>

        {/* Mid leg meta — duration + stops */}
        <li className="flex items-center gap-2.5 text-xs text-muted-foreground">
          <span className="flex w-3.5 justify-center" aria-hidden="true">
            <Plane className="h-3.5 w-3.5 -rotate-45" />
          </span>
          <span className="tabular-nums">
            {[dur, stopsLabel(flight.stops)].filter(Boolean).join(" · ")}
          </span>
        </li>

        {/* Arrive */}
        <li className="flex gap-2.5">
          <span className="mt-0.5 flex flex-col items-center" aria-hidden="true">
            <Circle className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />
          </span>
          <div className="min-w-0">
            <div className="font-mono tabular-nums font-semibold text-foreground">
              {arriveT}
              {arriveT !== "—" && nextDay && (
                <span className="ml-1 text-xs font-medium text-indigo-600">+1</span>
              )}
            </div>
            <div className="truncate text-muted-foreground">
              {dest.name}
              {dest.code && <span className="ml-1 font-mono tabular-nums">({dest.code})</span>}
            </div>
          </div>
        </li>
        </ol>
      )}

      {/* Operating detail — what we have, honestly. */}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 border-t pt-3 text-xs">
        <div className="flex justify-between gap-2 sm:block">
          <dt className="text-muted-foreground">Airline</dt>
          <dd className="font-medium text-foreground">{airlineName}</dd>
        </div>
        <div className="flex justify-between gap-2 sm:block">
          <dt className="text-muted-foreground">Flight</dt>
          <dd className="font-medium tabular-nums text-foreground">
            {flightNo ? <>Flight <span className="font-mono">{flightNo}</span></> : "—"}
          </dd>
        </div>
        <div className="flex justify-between gap-2 sm:block">
          <dt className="text-muted-foreground">Aircraft</dt>
          <dd
            className={
              "font-medium " +
              (flight.aircraft ? "text-foreground" : "text-muted-foreground")
            }
          >
            {aircraft}
          </dd>
        </div>
        <div className="flex justify-between gap-2 sm:block">
          <dt className="text-muted-foreground">Cabin</dt>
          <dd
            className={
              "font-medium " + (cabin ? "text-foreground" : "text-muted-foreground")
            }
          >
            {cabin || "—"}
          </dd>
        </div>
      </dl>

      {/* Price + source + perk */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
        <span className="flex items-center gap-1.5">
          <span className="font-mono tabular-nums text-base font-bold text-foreground">
            {priceLabel ?? "—"}
          </span>
          <SourceLed source={flight.source} withLabel />
        </span>
      </div>
      {flight.perk && (
        <div className="text-xs text-muted-foreground">{flight.perk.label}</div>
      )}

      {/* Emissions + amenities — rendered ONLY when the backend supplies them. */}
      <FlightExtras
        emissionsKgCo2e={flight.emissionsKgCo2e}
        amenities={flight.amenities}
      />

      {/* Honest gap note for multi-stop with NO segment data — we don't yet
          capture layover/fare/emissions for these. Suppressed once real
          segments exist. */}
      {multiStop && !segments && (
        <p className="rounded-md bg-muted/50 px-2 py-1.5 text-[11px] leading-relaxed text-muted-foreground">
          Segment, fare-class, and emissions detail coming soon.
        </p>
      )}
    </div>
  );
}
